// WP 10.0 — a thread's title, for Ask's one-line header (10.1).
//
// `persist.ts` used to store the first 80 characters of the raw line —
// "Can you check if t…" on Doug's phone, cut mid-sentence with no regard for
// what the words were. This is that decision, alone: no imports, so it can
// be tested the way `rowMeta.ts`'s dependency-free rules are, and so the
// Worker's title and any future caller's agree because they call the same
// function rather than keeping two copies of the same truncation.

/** Doug's own number: short enough for one line at the header's size, long
 *  enough to still say what the thread is about.
 *
 *  WP 10.7 (10.6 finding 2): 40 was measured against a two-button row.
 *  10.1 added a third 44px button (Account) to the same row, and 10.6's own
 *  render showed a 34-character title still overflowing the 214px column CSS
 *  ellipsis then had to save — cutting mid-word, the thing this file exists
 *  to prevent. 28 is what the row's real width now holds, so the
 *  word-boundary cut below is what usually decides the truncation point
 *  again, with CSS ellipsis back to being the safety net for a single
 *  over-long word rather than the common case. */
export const TITLE_MAX = 28;

/** A greeting or a request-marker Doug's own phrasing opens with — cut
 *  because it is never what the thread is about, only how he asked. Matched
 *  at the start only, case-insensitively, with an optional comma before the
 *  space that follows. */
const LEADING_FILLER = /^(?:can you|could you|please|hey|hi)[,]?\s+/i;

/**
 * The thread's title: the first line Doug sent, trimmed to what it is about.
 *
 * Collapses whitespace, drops one leading greeting or request-marker (and
 * a comma after it, if he wrote one — "Hey, could you check…"), capitalises
 * what is left, then either cuts it at the last whole word within
 * `TITLE_MAX` characters and marks the cut with "…", or — when it already
 * fits — drops a trailing "?" or "." rather than carrying a question mark
 * into a heading nothing else in the row ends with punctuation.
 */
export function threadTitle(text: string): string {
  let s = text.trim().replace(/\s+/g, ' ');
  const stripped = s.replace(LEADING_FILLER, '');
  if (stripped) s = stripped;
  if (s) s = s[0].toUpperCase() + s.slice(1);

  if (s.length <= TITLE_MAX) {
    return /[?.]$/.test(s) ? s.slice(0, -1) : s;
  }

  const window = s.slice(0, TITLE_MAX);
  const boundary = window.lastIndexOf(' ');
  const cut = boundary > 0 ? window.slice(0, boundary) : window;
  return `${cut}…`;
}
